/**
 * One entry of an application's onboarding timeline: the status it moved into,
 * when, and who moved it.
 *
 * ⚠️ API SHAPE ASSUMPTION — the Go backend does not expose an onboarding audit
 * log endpoint yet. These fields, and the `/v22/pias/onboarding/auditlog`
 * route wired in `environment.apis.getOnboardingAuditLog`, are the shape this
 * screen expects and must be cross-checked against the backend repo once the
 * endpoint lands. `normalizeAuditLogEntry` below absorbs the most likely
 * naming variants so the dialog degrades to a "no history" state instead of
 * breaking if the struct differs.
 */
export interface OnboardingAuditLog {
    status: string;
    date: string;
    userRole: string;
    userName: string;
    remarkContent?: string;
}

/** Payload handed to `OnboardingAuditLogDialog` when it is opened from the table. */
export interface OnboardingAuditLogDialogData {
    id: number;
    applicantName: string;
    designation: string;
    currentStatus: string;
    dateOfApplication: string;
}

/**
 * Maps one raw audit row onto `OnboardingAuditLog`, tolerating the naming
 * variants used elsewhere in the onboarding APIs (`userStatus`, `updatedNow`,
 * `requesterName`, …). Returns null for a row with no usable status so a
 * partially populated response still renders the rows it can.
 */
export function normalizeAuditLogEntry(row: Record<string, unknown>): OnboardingAuditLog | null {
    const pick = (...keys: string[]): string => {
        for (const key of keys) {
            const value = row[key];
            if (typeof value === 'string' && value.trim()) {
                return value.trim();
            }
            if (typeof value === 'number') {
                return value.toString();
            }
        }
        return '';
    };

    // `status` may arrive either flat or wrapped the way the list API wraps it.
    const nestedStatus = row['status'] as { userStatus?: string } | string | undefined;
    const status = typeof nestedStatus === 'object' && nestedStatus?.userStatus
        ? nestedStatus.userStatus
        : pick('status', 'userStatus', 'actionName', 'action');

    if (!status) {
        return null;
    }

    return {
        status:pick('action'),
        date: pick('date','actionDate', 'actionedOn', 'updatedNow', 'updatedAt', 'createdAt', 'dateOfApplication'),
        userRole: pick('userRole', 'role', 'actionedByRole'),
        userName: pick('owner','userName', 'actionedBy', 'requesterName', 'fullNameNric', 'preferredName'),
        remarkContent: pick('remarkContent', 'remarks', 'comment')
    };
}
